// PortalCreditAuthorization — /portal/credit-authorization
//
// The FCRA written authorization for clients who joined before intake started
// collecting it, or who need to re-sign after a wording change. The text comes
// from the server so what they read is exactly the version we record.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CheckCircle2, FileSignature } from "lucide-react";
import { PortalShell } from "../components/PortalShell";

async function api(path: string, opts: { method?: string; body?: any } = {}) {
  const res = await fetch(path, {
    method: opts.method || "GET",
    credentials: "include",
    headers: opts.body ? { "Content-Type": "application/json" } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) { window.location.href = "/portal/login"; throw new Error("unauthorized"); }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  return data;
}

const when = (d: string) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

export default function PortalCreditAuthorizationPage() {
  const [state, setState] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState("");

  async function load() {
    try { setState(await api("/api/portal/credit-authorization")); }
    catch (e: any) { if (e.message !== "unauthorized") setErr(e?.message || "Could not load this page."); }
  }
  useEffect(() => { load(); }, []);

  async function sign() {
    setBusy(true); setErr(null);
    try {
      await api("/api/portal/credit-authorization", { method: "POST", body: { accepted: true, signed_name: name.trim() } });
      setAgree(false); setName("");
      await load();
    } catch (e: any) { setErr(e?.message || "Could not save your signature."); }
    finally { setBusy(false); }
  }

  const t = state?.text;
  const signed = state?.signed;

  return (
    <PortalShell actions={<Link to="/portal/dashboard" className="portal-btn-ghost"><ArrowLeft size={14} aria-hidden="true" /> Dashboard</Link>}>
      <h1 className="portal-h1" style={{ marginBottom: 4 }}>Credit authorization</h1>
      {!state && !err && <p className="portal-sub">Loading…</p>}
      {err && !state && <p className="portal-sub" style={{ color: "var(--ne-red-dark)" }}>{err}</p>}

      {state && (
        <>
          <p className="portal-sub" style={{ marginBottom: 20 }}>{t.intro}</p>

          {signed && signed.current && (
            <article className="portal-card" style={{ marginBottom: 16 }}>
              <p style={{ margin: 0, color: "var(--ne-success)" }}>
                <CheckCircle2 size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
                Signed on {when(signed.signed_at)} by {signed.signed_name}. Nothing else is needed.
              </p>
              <p className="portal-sub on-card" style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                You can withdraw this at any time — just tell your advisor in writing and we'll stop
                pulling your credit.
              </p>
            </article>
          )}

          <article className="portal-card" style={{ marginBottom: 16 }}>
            <div className="portal-card-heading"><h2>{t.title}</h2></div>
            <ol style={{ margin: "10px 0 0", paddingLeft: 20, display: "grid", gap: 10 }}>
              {t.points.map((p: string, i: number) => (
                <li key={i} className="portal-sub on-card" style={{ fontSize: 13.5, lineHeight: 1.6 }}>{p}</li>
              ))}
            </ol>
            <p className="portal-sub on-card" style={{ fontSize: 12.5, marginTop: 14, marginBottom: 0 }}>
              <ShieldCheck size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
              {t.footer}
            </p>
          </article>

          {(!signed || !signed.current) && (
            <article className="portal-card">
              <div className="portal-card-heading"><h2>Sign</h2></div>
              <p className="portal-sub on-card" style={{ fontSize: 13 }}>{t.signature_prompt}</p>
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "14px 0", cursor: "pointer" }}>
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--ne-red-bright)" }} />
                <span className="portal-sub on-card" style={{ fontSize: 13.5, margin: 0 }}>
                  I have read the authorization above and I authorize {state.company} to obtain my
                  credit report and score.
                </span>
              </label>
              <label className="portal-field" style={{ display: "block" }}>
                <span className="portal-sub on-card" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Your full legal name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
                  placeholder={state.legal_name || "First and last name"} style={{ width: "100%" }} />
              </label>
              {err && <p className="portal-sub" style={{ color: "var(--ne-red-dark)", marginTop: 10 }}>{err}</p>}
              <button className="portal-btn-primary wide" style={{ marginTop: 14 }}
                disabled={busy || !agree || name.trim().length < 2} onClick={sign}>
                <FileSignature size={15} aria-hidden="true" />
                {busy ? "Saving…" : "Sign and submit"}
              </button>
            </article>
          )}
        </>
      )}
    </PortalShell>
  );
}
